import type { UserOutput } from "@orsane/schema/users";

/**
 * What the auth client knows about the signed-in user.
 *
 * - ready: get-session returned a user. A missing organization membership does not
 *   look like this — the user is still present, and the API answers 401 on its own.
 * - pending: the session request has not settled. The route guard may already have
 *   a user; this is not a sign-out.
 * - signed-out: the session settled with no user (null body, or a 401 that clears it).
 *   This is the state after a focus refetch once the session is gone.
 * - unavailable: the session request failed for another reason and there is no cached
 *   user. Do not treat that as a sign-out.
 */
export type SessionAvailability = "ready" | "pending" | "signed-out" | "unavailable";

export function classifySession(snapshot: {
    user: { id: string } | null;
    isPending: boolean;
    hasError: boolean;
    errorStatus: number | null;
}): SessionAvailability {
    if (snapshot.user) return "ready";
    if (snapshot.isPending) return "pending";
    if (snapshot.hasError && snapshot.errorStatus !== 401) return "unavailable";
    return "signed-out";
}

export function sessionErrorStatus(error: unknown): number | null {
    if (!error || typeof error !== "object" || !("status" in error)) return null;
    return typeof error.status === "number" ? error.status : null;
}

export function toUserOutput(
    user: { id: string; name: string; email: string; image?: string | null } | null | undefined
): UserOutput | null {
    if (!user) return null;
    return {
        id: user.id,
        name: user.name,
        email: user.email,
        image: user.image ?? null,
    };
}

/** Login URL that returns the user to the page they were on, matching the route guard. */
export function loginPath(currentHref: string): string {
    const current = new URL(currentHref);
    const login = new URL("/login", current.origin);
    login.searchParams.set("redirect", currentHref);
    return `${login.pathname}${login.search}`;
}

let redirectStarted = false;

export function redirectToLogin(currentHref: string = window.location.href): void {
    if (redirectStarted) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/login") return;
    redirectStarted = true;
    window.location.assign(loginPath(currentHref));
}
